import { useCallback, useEffect, useReducer } from 'react';
import { capitalize, sortBy } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { useFieldUpdater, useStatusUpdater } from '@brightmls/nextgen.lae.flc';
import {
  ControlField,
  useMetadata,
} from '../../../../contexts/MetadataContext';
import { PropertyField } from '../../../../system/field';
import { formatStreet } from '../../../../system/location';
import { Lookup } from '../../../../system/lookup';
import {
  HistoricalListingData,
  ListingData,
} from '../../../../system/recommendations';
import { hasValue, valueIsCustomOrNone } from '../utils';
import { FeatureFlags } from '../../../../flags';
import { useFeatureFlags } from '../../../../contexts/FeatureFlagsContext';
import { isValidFromDefinition } from '../../../../utils/validation';
import {
  ASSESSOR_SOURCE,
  Field,
  FieldsAndSourceList,
  ListingActionType,
  ListingMetrics,
  ListingMetricType,
  ListingRecord,
  ListingSection,
  ListingState,
  ListingStateKey,
  PreviousTemplateList,
  RadioGroupTypes,
  RecordTypes,
  RecordValue,
  UnselectedRecord,
  UnselectedRecordList,
} from '../utils/types';
import {
  getReadyToImportValue,
  getUnselectedFields,
  listingAssistantReducer,
} from '../utils/reducer';
import { hasNoConflictData, shouldReviewField } from '../utils/reducerUtils';
import { getRecordType, getStateValueFromRecord } from '../utils/recordUtils';

const useListingAssistant = () => {
  const initialState: ListingState = {
    [ListingStateKey.CONFIG]: {
      persistResolvedData: true,
      includeUnselectedListings: false,
    },
    [ListingStateKey.METRICS]: {},
    [ListingStateKey.ENABLED_RECORDS]: {} as Record<string, ListingRecord>,
    [ListingStateKey.RECORDS]: [],
    [ListingStateKey.RESOLVED_FIELDS]: {},
    [ListingStateKey.PREVIOUS_TEMPLATE_LIST]: {},
    [ListingStateKey.TEMPLATE_FIELDLIST]: {
      fields: {},
      sourceFields: {},
    },
    [ListingStateKey.RECORDS_FIELDLIST]: {
      fields: {},
      sourceFields: {},
    },
    [ListingStateKey.READY_TO_IMPORT_LIST]: {
      fields: {},
      sourceFields: {},
      sections: [],
    },
    [ListingStateKey.TEMPLATE]: {
      current: '',
    },
    [ListingStateKey.HIDDEN_FIELDS]: {
      fromListing: [],
      fromTax: [],
    },
  };
  const { flattenFields, field: getFieldMetadata, lookup } = useMetadata();
  const { hasFeature, getFeatureValue } = useFeatureFlags();
  const updateFields = useFieldUpdater();
  const updateStatus = useStatusUpdater();

  const [state, dispatch] = useReducer(listingAssistantReducer, initialState);

  const getTotal = useCallback(
    (type: any): number => {
      const metrics = state[ListingStateKey.METRICS] || {};
      return +metrics[type] || 0;
    },
    [state],
  );

  const updateFieldsFromTemplate = useCallback(() => {
    const template = state[ListingStateKey.TEMPLATE];
    const templateData = template[template.current];

    if (templateData) {
      dispatch({
        type: ListingActionType.UPDATE_TEMPLATE_FIELDS,
        payload: {},
      });
    }
  }, [state]);

  const toggleRecord = useCallback((record: ListingRecord) => {
    if (record.id) {
      updateStatus({ touched: true });
      dispatch({ type: ListingActionType.ENABLE_RECORD, payload: { record } });
    }
  }, []);

  const getRecords = useCallback(
    (render: Function) => {
      const records = state[ListingStateKey.RECORDS];
      return records.map((record: ListingRecord) => {
        const recordId: string = record.id;
        const stateMetrics: ListingMetrics =
          state[ListingStateKey.METRICS] || {};
        const enabledRecords = state[ListingStateKey.ENABLED_RECORDS] || {};
        const template = state[ListingStateKey.TEMPLATE];
        const totalFields = stateMetrics[template.current]?.[recordId] || 0;
        const enabledRecord = {
          ...enabledRecords[recordId],
          totalFields,
        };

        return render(enabledRecord);
      });
    },
    [state],
  );

  const getAllowedLookupValues = useCallback(
    (key: string) => {
      const metadata = getFieldMetadata(key);
      if (metadata?.Lookup?.length) {
        const allowedLookupValues = lookup(metadata.Lookup);
        const lookupValues = allowedLookupValues?.length
          ? allowedLookupValues.map((v: any) => v.LookupValue.toLowerCase())
          : [];
        return lookupValues;
      }
      return [];
    },
    [getFieldMetadata, lookup],
  );

  const getFilteredLookupValues = useCallback(
    (value: any, lookupValues: string[]) =>
      value.filter((v: any) => lookupValues.includes(v.toLowerCase?.() || v)),
    [],
  );

  const cleanRecordDataValues = useCallback(
    (recordData: ListingData) => {
      const cleanedRecordData: ListingData = {};
      for (const [key, value] of Object.entries(recordData || {})) {
        if (!hasValue(value)) continue;
        const lookupValues = getAllowedLookupValues(key);
        if (lookupValues?.length) {
          const valueIsArray = Array.isArray(value);
          const filteredValues = getFilteredLookupValues(
            valueIsArray ? value : [value],
            lookupValues,
          );
          if (filteredValues.length) {
            cleanedRecordData[key] = valueIsArray ? filteredValues : value;
          }
        } else if (key === PropertyField.ASSESSMENT_YEAR) {
          // Special Case: ASSESSMENT_YEAR for Tax Data
          const metadata = getFieldMetadata(key);
          let validation = isValidFromDefinition(
            {} as ControlField,
            metadata,
            value,
          );
          if (validation.isValid) {
            cleanedRecordData[key] = value;
          }
        } else {
          cleanedRecordData[key] = value;
        }
      }

      return {
        ...cleanedRecordData,
      };
    },
    [getAllowedLookupValues, getFieldMetadata, getFilteredLookupValues],
  );

  const formatInitialRecordData = useCallback(
    (recordData: ListingData) => {
      const cleanedRecordData = cleanRecordDataValues(recordData);
      const formattedData: ListingData = {};
      const streetName = cleanedRecordData[PropertyField.STREET_NAME];
      if (streetName?.length) {
        const lookups = {
          street_types: lookup(Lookup.STREET_TYPES)!,
          street_directions: lookup(Lookup.STREET_DIRECTIONS)!,
        };
        const formattedStreet = formatStreet(streetName, lookups);
        formattedData[PropertyField.STREET_NAME] =
          formattedStreet?.name || streetName;
      }

      return {
        ...cleanedRecordData,
        ...formattedData,
      };
    },
    [cleanRecordDataValues, lookup],
  );

  const processRecords = useCallback(
    (records: HistoricalListingData[]) => {
      const _records = sortBy(
        records,
        (r) =>
          -new Date(
            r.Data.MLS_LIST_DATE || r.Data.ORIGINAL_ENTRY_TIMESTAMP,
          ).getTime(),
      ).reduce(
        (recordsSet: any, record: any) => {
          const recordKey =
            record.Type === RecordTypes.TAX
              ? `${record.Data.PARCEL_ID}-${record.Data.FIPS_CODE}-${record.Year}`
              : record.Type === RecordTypes.AI
                ? `AI-${record.Year}`
                : record.Data.LISTING_ID;
          if (recordKey && recordsSet.keys[recordKey]) return recordsSet;
          recordsSet.keys[recordKey] = true;
          const formattedRecordData = formatInitialRecordData(record.Data);

          recordsSet.list = [
            ...recordsSet.list,
            {
              ...record,
              Data: formattedRecordData,
              id: uuidv4(),
            },
          ];
          return recordsSet;
        },
        { keys: {}, list: [] } as any,
      );

      dispatch({
        type: ListingActionType.SET_RECORDS,
        payload: { records: _records.list },
      });
    },
    [formatInitialRecordData, getFieldMetadata, state],
  );

  const processTemplate = useCallback(
    (templateName: string, templateData: any) => {
      let flattenedTemplate = state[ListingStateKey.TEMPLATE][templateName];

      if (!flattenedTemplate) {
        flattenedTemplate = sortBy(templateData?.groups, 'name').reduce(
          (templateGroups, group) => {
            const title = capitalize(group.name);
            if (templateGroups[title]) return templateGroups;
            templateGroups[title] = {
              title,
              flattenFields: flattenFields(group),
            };
            return templateGroups;
          },
          {},
        );
      }

      dispatch({
        type: ListingActionType.SET_TEMPLATE,
        payload: {
          templateName,
          templateData: flattenedTemplate,
        },
      });
    },
    [flattenFields, state],
  );

  const mapFields = useCallback(
    (render: Function, type?: string) => {
      const fields = state[ListingStateKey.TEMPLATE_FIELDLIST].fields;
      const fieldValues = Object.values(fields || {});
      let fieldsToRender = [...fieldValues] as Field[];

      if (type === ListingMetricType.NO_CONFLICT_FIELDS) {
        fieldsToRender = fieldsToRender.filter(hasNoConflictData);
      } else if (type === ListingMetricType.NEEDS_REVIEW) {
        fieldsToRender = fieldsToRender.filter(shouldReviewField);
      }

      if (fieldValues.length) {
        return fieldsToRender.map((field: Field, i: number) =>
          render(i, field),
        );
      }
      return null;
    },
    [state],
  );

  const mapSections = useCallback(
    (render: Function, type?: string) => {
      const fieldList: FieldsAndSourceList =
        state[ListingStateKey.RECORDS_FIELDLIST];
      let sectionsToRender = fieldList?.sections?.length
        ? [...fieldList.sections]
        : [];
      if (type === ListingMetricType.READY_TO_IMPORT) {
        const readyToImportFields: FieldsAndSourceList =
          state[ListingStateKey.READY_TO_IMPORT_LIST];
        sectionsToRender = readyToImportFields.sections?.length
          ? [...readyToImportFields.sections]
          : [];
      }

      if (sectionsToRender?.length) {
        return sectionsToRender.map((section: any, i: number) => {
          return render(i, section);
        });
      }

      return null;
    },
    [state],
  );

  const unselectedFields = useCallback(
    (
      sectionRender: Function,
      recordRender: Function,
      noRecordsRender: Function,
    ) => {
      const unselectedFieldsFromRecords: UnselectedRecordList =
        getUnselectedFields(state);
      const records = state[ListingStateKey.RECORDS];
      const enabledRecords = state[ListingStateKey.ENABLED_RECORDS];
      const includeUnselectedListings =
        state[ListingStateKey.CONFIG].includeUnselectedListings;
      const unselectedRecords: UnselectedRecord[] = records.reduce(
        (acc: UnselectedRecord[], record: ListingRecord) => {
          const recordId = record.id;
          const enabledRecord = enabledRecords[recordId];
          if (!includeUnselectedListings && !enabledRecord.isEnabled) {
            return acc;
          }

          return [
            ...acc,
            {
              ...unselectedFieldsFromRecords[recordId],
              recordId,
              label: enabledRecord.label,
            },
          ];
        },
        [],
      );

      if (!unselectedRecords.length) {
        return noRecordsRender();
      }

      return unselectedRecords.map((record: UnselectedRecord) => {
        const sectionsToRender = (record.listedSections?.sections || []).map(
          (section: ListingSection) => sectionRender(section, record.recordId),
        );
        return recordRender(record, sectionsToRender);
      });
    },
    [state],
  );

  const getSourceFieldValue = useCallback(
    (sourceFieldKey: string, recordId: string = '') => {
      if (recordId && sourceFieldKey) {
        const templateFieldList = state[ListingStateKey.TEMPLATE_FIELDLIST];
        const field: Field = templateFieldList.sourceFields[sourceFieldKey];

        if (field?.sourceField?.value) {
          return field.sourceField?.value;
        }
      }

      return null;
    },
    [state],
  );

  const getSourceField = useCallback(
    (field: Field | string): Field | string | null => {
      const key: string = 'string' === typeof field ? field : field?.key;
      const templateFieldList = state[ListingStateKey.TEMPLATE_FIELDLIST];
      const sourceField = templateFieldList.sourceFields[key] || null;
      return sourceField;
    },
    [state],
  );

  const resolveField = useCallback(
    (field: Field, recordValue: RecordValue) => {
      const { recordId, value: newValue } = recordValue;
      const valueFromRecord = getStateValueFromRecord(
        state,
        field.key,
        recordValue,
      );
      const templateFieldList = state[ListingStateKey.TEMPLATE_FIELDLIST];
      const enabledRecords = state[ListingStateKey.ENABLED_RECORDS];
      const key = field.key;
      if (templateFieldList?.fields[key] && valueFromRecord) {
        const record = enabledRecords[recordId] || {};
        dispatch({
          type: ListingActionType.RESOLVE_FIELD,
          payload: {
            field,
            value: {
              recordType: record.type,
              recordId,
              value: valueFromRecord.value,
            },
          },
        });
      } else {
        console.warn(
          'Warning, setting an invalid value from record: ',
          recordId,
        );
      }
    },
    [state],
  );

  const addField = useCallback((field: Field, recordValue: RecordValue) => {
    dispatch({
      type: ListingActionType.ADD_FIELD,
      payload: { field, recordValue },
    });
  }, []);

  const updateField = useCallback((field: Field, recordValue: RecordValue) => {
    dispatch({
      type: ListingActionType.UPDATE_FIELD,
      payload: { field, recordValue },
    });
  }, []);

  const removeField = useCallback((field: Field, recordValue: RecordValue) => {
    dispatch({
      type: ListingActionType.REMOVE_FIELD,
      payload: { field, recordValue },
    });
  }, []);

  const persistResolvedFields = useCallback(() => {
    dispatch({
      type: ListingActionType.PERSIST_RESOLVED,
      payload: {},
    });
  }, [state]);

  const removeFieldAndSource = useCallback(
    (fieldKey: string) => {
      const sourceField = getSourceField(fieldKey);
      const updates: Record<string, any> = {};

      if (sourceField) {
        const sourceKey =
          typeof sourceField === 'string' ? sourceField : sourceField?.key;
        updates[sourceKey as string] = null;
      }
      updates[fieldKey] = null;

      updateFields(updates);
    },
    [getSourceField, updateFields],
  );

  const saveFields = useCallback(() => {
    const readyToImportFieldList: FieldsAndSourceList =
      state[ListingStateKey.READY_TO_IMPORT_LIST];
    const previousTemplateList: PreviousTemplateList =
      state[ListingStateKey.PREVIOUS_TEMPLATE_LIST];
    const fields: Field[] = Object.values(readyToImportFieldList.fields || {});
    const fieldsToUpdate: Record<string, any> = {};
    const fieldsToRemove: Record<string, boolean> = {};
    fields.forEach((field: Field) => {
      const recordValue: RecordValue | null = getReadyToImportValue(field);

      if (recordValue && !valueIsCustomOrNone(recordValue.value)) {
        const recordId = recordValue.recordId;
        const sourceField = getSourceField(field);
        const sourceKey =
          typeof sourceField === 'string' ? sourceField : sourceField?.key;
        const isTaxRecord =
          recordId?.length && getRecordType(recordId, state, RecordTypes.TAX);

        if (sourceKey && isTaxRecord) {
          fieldsToUpdate[sourceKey] = ASSESSOR_SOURCE;
        } else if (sourceKey && !isTaxRecord) {
          const sourceFieldValue = getSourceFieldValue(
            sourceKey,
            recordId as string,
          );
          if (sourceFieldValue) {
            fieldsToUpdate[sourceKey] = sourceFieldValue;
          } else {
            fieldsToRemove[sourceKey] = true;
          }
        }

        fieldsToUpdate[field.key] = recordValue.value;
      }

      if (recordValue?.value === RadioGroupTypes.NONE) {
        fieldsToRemove[field.key] = true;
      }
    });

    Object.keys(previousTemplateList).forEach((fieldKey: string) => {
      const readyToImportField = readyToImportFieldList.fields[fieldKey];
      const isSourceField = readyToImportFieldList.sourceFields[fieldKey];

      if (!readyToImportField || isSourceField) {
        fieldsToRemove[fieldKey] = true;
        return;
      }

      const fieldHasValues = readyToImportField?.readyToImportValues?.hasValues;
      if (!fieldHasValues) {
        fieldsToRemove[fieldKey] = true;
      }
    });

    // Combine removes and updates into single batch
    const allUpdates: Record<string, any> = { ...fieldsToUpdate };

    // Set removed fields to null
    Object.keys(fieldsToRemove).forEach((key: string) => {
      allUpdates[key] = null;
    });

    // Single batch update
    if (Object.keys(allUpdates).length > 0) {
      updateFields(allUpdates);
    }
  }, [getSourceField, getSourceFieldValue, updateFields, state]);

  const stateTemplate = state[ListingStateKey.TEMPLATE];

  useEffect(() => {
    updateStatus({ touched: true });
    updateFieldsFromTemplate();
  }, [stateTemplate]);

  useEffect(() => {
    const remainingFieldsTotal = getTotal(
      ListingMetricType.REMAINING_REVIEW_FIELDS,
    );
    const readyToImportTotal = getTotal(ListingMetricType.READY_TO_IMPORT);
    const records = state[ListingStateKey.RECORDS];

    if (
      records.length > 0 &&
      remainingFieldsTotal === 0 &&
      readyToImportTotal > 0
    ) {
      updateStatus({ ready: true });
    } else if (records.length === 0) {
      updateStatus({ ready: true, touched: false });
    } else {
      updateStatus({ ready: false });
    }
  }, [getTotal, updateStatus, state]);

  useEffect(() => {
    if (hasFeature(FeatureFlags.REMOVE_FROM_LISTING_DATA)) {
      const hiddenFieldList = getFeatureValue(
        FeatureFlags.REMOVE_FROM_LISTING_DATA,
      );
      if (Array.isArray(hiddenFieldList) && hiddenFieldList.length) {
        dispatch({
          type: ListingActionType.SET_HIDDEN_FIELDS,
          payload: {
            type: 'fromListing',
            fields: hiddenFieldList,
          },
        });
      }
    }

    if (hasFeature(FeatureFlags.REMOVE_FROM_TAX_DATA)) {
      const hiddenFieldList = getFeatureValue(
        FeatureFlags.REMOVE_FROM_TAX_DATA,
      );
      if (Array.isArray(hiddenFieldList) && hiddenFieldList.length) {
        dispatch({
          type: ListingActionType.SET_HIDDEN_FIELDS,
          payload: {
            type: 'fromTax',
            fields: hiddenFieldList,
          },
        });
      }
    }

    dispatch({
      type: ListingActionType.INIT,
      payload: {
        getFieldMetadata,
      },
    });
  }, []);

  return {
    ...ListingMetricType,
    addField,
    getRecords,
    getTotal,
    mapFields,
    mapSections,
    persistResolvedFields,
    removeField,
    resolveField,
    saveFields,
    setRecords: processRecords,
    setTemplate: processTemplate,
    state,
    toggleRecord,
    updateField,
    unselectedFields,
  };
};

export default useListingAssistant;
